import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  CircularProgress,
  Icon
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Formik, FormikProps, Form } from 'formik';
import * as Yup from 'yup';
import theme from '../../../../assets/theme';
import FaiBox from "../../../../MuiComponents/FaiBox";
import FaiTypography from "../../../../MuiComponents/FaiTypography";
import FaiButton from '../../../../MuiComponents/FaiButton';
import FormField from '../../../../components/FormField';
import FaiSelect from '../../../../MuiComponents/FaiSelect';
import FaiInput from '../../../../MuiComponents/FaiInput';
import CouponController from '../../../../controllers/couponController';
import { Coupon } from '../../../../types/couponTypes';
import { AxiosResponse } from 'axios';

interface EditCouponModalProps {
  open: boolean;
  onClose: () => void;
  couponId: string | null;
  onCouponUpdated: () => void;
}

interface SelectOption {
  value: string;
  label: string;
}

// Define a response format that matches the actual API response
interface CouponApiResponse {
  id: string;
  couponType: string;
  brand: string;
  campaignName: string;
  description: string;
  couponCode: string;
  validFromDateTime: string;
  validTillDateTime: string;
  minimumPrice: string | null;
  maximumPrice: string | null;
  percentageFlat: string | null;
  uptoDetails: any;
  platformLoyalCouponBasedOnType: any;
  numberOfOrders: any;
  totalPurchaseTillDate: any;
  media: any;
  masterCategories: any;
  categories: any;
  products: any;
  status: string;
  createdAt: string | null;
  updatedAt: string | null;
}

// Define a more specific API response type that might contain nested data
interface ApiResponse<T> {
  status: number;
  message: string | null;
  errors: any | null;
  data: T;
  timeStamp: number;
}

const validationSchema = Yup.object().shape({
  brandName: Yup.string().required('Brand name is required'),
  couponCode: Yup.string().required('Coupon code is required'),
  campaignName: Yup.string().required('Campaign name is required'),
  discountType: Yup.string().required('Discount type is required'),
  discountPercentage: Yup.string().required('Discount is required'),
  minimumValue: Yup.string().required('Minimum value is required'),
  maximumValue: Yup.string().required('Maximum value is required'),
  startDate: Yup.string().required('Start date is required'),
  endDate: Yup.string().required('End date is required'),
  description: Yup.string().required('Description is required'),
});

function EditCouponModal({ open, onClose, couponId, onCouponUpdated }: EditCouponModalProps) {
  const { typography, palette } = theme;
  const [loading, setLoading] = useState<boolean>(true);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<SelectOption[]>([]);

  const [initialValues, setInitialValues] = useState<Coupon>({
    brandName: "",
    productId: "",
    couponCode: "",
    campaignName: "",
    discountType: "",
    discountPercentage: "",
    minimumValue: "",
    maximumValue: "",
    startDate: "",
    endDate: "",
    description: "",
    status: "ACTIVE",
    masterCategories: []
  });

  useEffect(() => {
    const fetchCouponData = async () => {
      if (!couponId || !open) return;
      
      setLoading(true);
      try {
        // Get the coupon data
        const response: AxiosResponse = await CouponController.getCoupon(couponId);
        console.log('Coupon data:', response.data);
        
        // The API response should be in the format: { status, message, errors, data, timeStamp }
        const apiResponse = response.data as ApiResponse<CouponApiResponse>;
        
        // Extract the coupon data from the response
        if (!apiResponse.data) {
          console.error('No data found in API response');
          return;
        }
        
        const apiCoupon = apiResponse.data;
        
        // Format dates to YYYY-MM-DD for the date inputs
        const formatDate = (dateString: string) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          return date.toISOString().split('T')[0];
        };

        // Map the API response to our Coupon interface
        const mappedCoupon: Coupon = {
          brandName: apiCoupon.brand || '',
          productId: '',  // This may need to be populated from products or categories
          couponCode: apiCoupon.couponCode || '',
          campaignName: apiCoupon.campaignName || '',
          discountType: apiCoupon.couponType || '',
          discountPercentage: apiCoupon.percentageFlat || '',
          minimumValue: apiCoupon.minimumPrice || '',
          maximumValue: apiCoupon.maximumPrice || '',
          startDate: formatDate(apiCoupon.validFromDateTime),
          endDate: formatDate(apiCoupon.validTillDateTime),
          description: apiCoupon.description || '',
          status: apiCoupon.status || 'ACTIVE',
          masterCategories: []
        };
        
        // Set the initial values for the form
        setInitialValues(mappedCoupon);
        
        // Create category options if masterCategories exists
        if (apiCoupon.masterCategories) {
          const categoryOpts = Array.isArray(apiCoupon.masterCategories) ? 
            apiCoupon.masterCategories.map((cat: any) => ({
              value: typeof cat === 'object' ? cat.id : cat,
              label: typeof cat === 'object' ? cat.name : cat
            })) : [];
          setCategories(categoryOpts);
        }
      } catch (error) {
        console.error('Error fetching coupon:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCouponData();
  }, [couponId, open]);

  const handleSubmit = async (values: Coupon, { setSubmitting }: any) => {
    if (!couponId) return;
    
    setSubmitLoading(true);
    try {
      // Map form values back to API format
      const payload = {
        brand: values.brandName,
        couponType: values.discountType,
        couponCode: values.couponCode,
        campaignName: values.campaignName,
        percentageFlat: values.discountPercentage,
        minimumPrice: values.minimumValue,
        maximumPrice: values.maximumValue,
        validFromDateTime: values.startDate,
        validTillDateTime: values.endDate,
        description: values.description,
        status: values.status,
        masterCategories: values.masterCategories || []
      };
      
      await CouponController.updateCoupon(couponId, payload);
      onCouponUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating coupon:', error);
    } finally {
      setSubmitLoading(false);
      setSubmitting(false);
    }
  };

  // Merchants only manage MERCHANT_* coupons; PLATFORM_* are admin-only.
  const discountOptions: SelectOption[] = [
    { value: "MERCHANT_PERCENTAGE_FULL_DISCOUNT", label: "MERCHANT_PERCENTAGE_FULL_DISCOUNT" },
    { value: "MERCHANT_PERCENTAGE_UPTO_DISCOUNT", label: "MERCHANT_PERCENTAGE_UPTO_DISCOUNT" },
    { value: "MERCHANT_FLAT_FULL_DISCOUNT", label: "MERCHANT_FLAT_FULL_DISCOUNT" },
    { value: "MERCHANT_FLAT_UPTO_DISCOUNT", label: "MERCHANT_FLAT_UPTO_DISCOUNT" }
  ];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <FaiBox display="flex" justifyContent="space-between" alignItems="center">
          <FaiTypography variant="h5" fontWeight={typography.fontWeightMedium}>
            Edit Coupon
          </FaiTypography>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </FaiBox>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <FaiBox display="flex" justifyContent="center" alignItems="center" height="300px">
            <CircularProgress />
          </FaiBox>
        ) : (
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {(props: FormikProps<Coupon>) => {
              const { 
                values, 
                touched, 
                errors, 
                handleChange, 
                handleBlur, 
                setFieldValue,
                handleSubmit 
              } = props;
              
              return (
                <Form>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormField
                        label="Brand Name"
                        name="brandName"
                        placeholder="Enter Brand Name"
                        value={values.brandName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.brandName && Boolean(errors.brandName)}
                        helperText={touched.brandName && errors.brandName}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormField
                        label="Campaign Name"
                        name="campaignName"
                        placeholder="Enter Campaign Name"
                        value={values.campaignName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.campaignName && Boolean(errors.campaignName)}
                        helperText={touched.campaignName && errors.campaignName}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormField
                        label="Coupon Code"
                        name="couponCode"
                        placeholder="Enter Coupon Code"
                        value={values.couponCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.couponCode && Boolean(errors.couponCode)}
                        helperText={touched.couponCode && errors.couponCode}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FaiBox mb={3}>
                        <FaiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <FaiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            Discount Type
                          </FaiTypography>
                        </FaiBox>
                        <FaiSelect
                          placeholder="Choose Type"
                          name="discountType"
                          value={values.discountType}
                          options={discountOptions}
                          onChange={(selectedOption: SelectOption) => {
                            setFieldValue("discountType", selectedOption.value);
                          }}
                        />
                      </FaiBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormField
                        label="Discount"
                        name="discountPercentage"
                        placeholder="Enter percentage"
                        value={values.discountPercentage}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.discountPercentage && Boolean(errors.discountPercentage)}
                        helperText={touched.discountPercentage && errors.discountPercentage}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormField
                        type="date"
                        label="Start Date"
                        name="startDate"
                        value={values.startDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.startDate && Boolean(errors.startDate)}
                        helperText={touched.startDate && errors.startDate}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormField
                        type="date"
                        label="End Date"
                        name="endDate"
                        value={values.endDate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.endDate && Boolean(errors.endDate)}
                        helperText={touched.endDate && errors.endDate}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FaiBox mb={1}>
                        <FaiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <FaiTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                            textTransform="capitalize"
                          >
                            Min - Max Amount
                          </FaiTypography>
                        </FaiBox>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <FaiInput
                              placeholder="Enter Minimum Amount"
                              name="minimumValue"
                              value={values.minimumValue}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.minimumValue && Boolean(errors.minimumValue)}
                              helperText={touched.minimumValue && errors.minimumValue}
                              startAdornment={<Icon sx={{ marginRight: "1rem" }}>currency_rupee_outlined</Icon>}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FaiInput
                              placeholder="Enter Maximum Amount"
                              name="maximumValue"
                              value={values.maximumValue}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.maximumValue && Boolean(errors.maximumValue)}
                              helperText={touched.maximumValue && errors.maximumValue}
                              startAdornment={<Icon sx={{ marginRight: "1rem" }}>currency_rupee_outlined</Icon>}
                            />
                          </Grid>
                        </Grid>
                      </FaiBox>
                    </Grid>
                    <Grid item xs={12}>
                      <FormField
                        multiline
                        rows={4}
                        label="Coupon Description"
                        name="description"
                        placeholder="Enter Description"
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.description && Boolean(errors.description)}
                        helperText={touched.description && errors.description}
                      />
                    </Grid>
                  </Grid>
                  
                  <FaiBox mt={3} display="flex" justifyContent="flex-end">
                    <FaiButton 
                      variant="outlined"
                      color="secondary"
                      onClick={onClose}
                      sx={{ marginRight: 2 }}
                    >
                      Cancel
                    </FaiButton>
                    <FaiButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={submitLoading}
                    >
                      {submitLoading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
                    </FaiButton>
                  </FaiBox>
                </Form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default EditCouponModal;
